import React, { useRef, useState } from 'react';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/plain',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

const MAX_FILES = 5;

/**
 * Drag-and-drop upload panel for the Document Chat page.
 *
 * Allows the user to select or drop 1–5 documents, previews the chosen
 * file names as removable badges, and shows a progress indicator while
 * the upload is in flight.
 *
 * @param {Object}   props
 * @param {Function} props.onUpload      - Called with the selected File[] when the
 *                                         user clicks "Start Chat". The parent is
 *                                         responsible for the actual API call and
 *                                         should pass `uploading` accordingly.
 * @param {boolean}  props.uploading     - When true, the start button shows a spinner.
 */
const DocChatUploadPanel = ({ onUpload, uploading = false }) => {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // ------------------------------------------------------------------
  // File validation helpers
  // ------------------------------------------------------------------
  const validateAndAdd = (incoming) => {
    setError('');
    const combined = [...files];
    const rejected = [];

    for (const f of incoming) {
      if (combined.length >= MAX_FILES) {
        rejected.push(f.name);
        continue;
      }
      if (!ACCEPTED_TYPES.includes(f.type)) {
        rejected.push(`${f.name} (unsupported type)`);
        continue;
      }
      // Avoid duplicates by name
      if (combined.some((existing) => existing.name === f.name)) continue;
      combined.push(f);
    }

    setFiles(combined);

    if (rejected.length > 0) {
      setError(
        `Skipped: ${rejected.join(', ')}. ` +
          `Accepted formats: PDF, DOCX, XLSX, TXT, CSV, PNG, JPG, GIF, WEBP.`
      );
    }
  };

  const removeFile = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  // ------------------------------------------------------------------
  // Drag-and-drop handlers
  // ------------------------------------------------------------------
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    validateAndAdd(dropped);
  };

  // ------------------------------------------------------------------
  // Input handler
  // ------------------------------------------------------------------
  const handleInputChange = (e) => {
    validateAndAdd(Array.from(e.target.files));
    // Reset the input so the same file can be re-selected after removal
    e.target.value = '';
  };

  // ------------------------------------------------------------------
  // Submit
  // ------------------------------------------------------------------
  const handleSubmit = () => {
    if (files.length === 0) {
      setError('Please select at least one document before starting a chat.');
      return;
    }
    onUpload(files);
  };

  return (
    <div className="doc-chat-upload-panel">
      {/* Drop-zone */}
      <div
        className={`border rounded p-4 text-center mb-3 ${
          dragOver ? 'border-primary bg-primary bg-opacity-10' : 'border-dashed border-secondary'
        }`}
        style={{ cursor: 'pointer', borderStyle: 'dashed' }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <i className="bi bi-cloud-arrow-up fs-2 text-secondary d-block mb-2" />
        <p className="mb-1 fw-semibold">Drag &amp; drop documents here</p>
        <p className="text-muted small mb-0">
          or <span className="text-primary">click to browse</span> &mdash; up to {MAX_FILES} files
        </p>
        <p className="text-muted" style={{ fontSize: '0.75rem' }}>
          PDF · DOCX · XLSX · TXT · CSV · PNG · JPG
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_TYPES.join(',')}
          className="d-none"
          onChange={handleInputChange}
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert-warning alert-dismissible py-2 small mb-3">
          <i className="bi bi-exclamation-triangle me-1" />
          {error}
          <button
            type="button"
            className="btn-close btn-sm"
            onClick={() => setError('')}
            aria-label="Close"
          />
        </div>
      )}

      {/* Selected file badges */}
      {files.length > 0 && (
        <div className="mb-3">
          <p className="small text-muted mb-2">
            {files.length} / {MAX_FILES} document{files.length !== 1 ? 's' : ''} selected
          </p>
          <div className="d-flex flex-wrap gap-2">
            {files.map((f) => (
              <span
                key={f.name}
                className="badge d-flex align-items-center gap-1 bg-secondary text-white"
                style={{ fontSize: '0.8rem' }}
              >
                <i className="bi bi-file-earmark" />
                <span
                  style={{
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={f.name}
                >
                  {f.name}
                </span>
                <button
                  type="button"
                  className="btn-close btn-close-white ms-1"
                  style={{ fontSize: '0.6rem' }}
                  aria-label={`Remove ${f.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(f.name);
                  }}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Start Chat button */}
      <button
        className="btn btn-primary w-100"
        onClick={handleSubmit}
        disabled={uploading || files.length === 0}
      >
        {uploading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Uploading…
          </>
        ) : (
          <>
            <i className="bi bi-chat-text me-2" />
            Start Chat
          </>
        )}
      </button>
    </div>
  );
};

export default DocChatUploadPanel;
